import React, { useState, useEffect } from 'react';
import axios from 'axios';

function ClassModal({ classData, discipline = 'stem', onClose, onSave }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    grades: []
  });
  const [availableGrades, setAvailableGrades] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Load available grades for this discipline
  useEffect(() => {
    const loadGrades = async () => {
      try {
        const response = await axios.get(`/outcomes/api/grades/${discipline}`);
        // Response is now an array of grade strings: ['PK', 'K', '1', '2', ...]
        setAvailableGrades(response.data || []);
        setError('');
      } catch (err) {
        console.error('Failed to load grades:', err);
        setError('Failed to load available grades');
        setAvailableGrades([]);
      }
    };

    loadGrades();
  }, [discipline]);

  useEffect(() => {
    if (classData) {
      setFormData({
        name: classData.name,
        description: classData.description || '',
        grades: Array.isArray(classData.grades) ? classData.grades : []
      });
    } else {
      setFormData({
        name: '',
        description: '',
        grades: []
      });
    }
  }, [classData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleGradeToggle = (gradeName) => {
    setFormData(prev => ({
      ...prev,
      grades: prev.grades.includes(gradeName)
        ? prev.grades.filter(g => g !== gradeName)
        : [...prev.grades, gradeName]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name) {
      alert('Please fill in required fields');
      return;
    }

    setSubmitting(true);

    try {
      const payload = { ...formData, discipline };
      if (classData) {
        await axios.put(`/outcomes/api/classes/${classData.id}`, payload);
      } else {
        await axios.post('/outcomes/api/classes', payload);
      }
      onSave();
    } catch (error) {
      console.error('Failed to save class:', error);
      alert('Failed to save class');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{classData ? 'Edit Class' : 'Add New Class'}</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="error-alert">{error}</div>}
            
            <div className="form-group">
              <label>Class Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., 6th Grade STEM - Fall 2024"
                required
              />
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Optional: Add details about this class (semester, year, special focus, etc.)"
              />
            </div>

            <div className="form-group">
              <label>Grade Levels</label>
              {availableGrades.length === 0 ? (
                <div className="text-muted">No grades available for this discipline</div>
              ) : (
                <div className="grades-checkboxes">
                  {availableGrades.map(grade => (
                    <label key={grade} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.grades.includes(grade)}
                        onChange={() => handleGradeToggle(grade)}
                      />
                      <span>Grade {grade === 'PK' ? 'PK' : grade === 'K' ? 'K' : grade}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="form-helper">
              <strong>Tip:</strong> Select all grade levels this class covers.
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="cancel-btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="submit-btn" disabled={submitting}>
              {submitting ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ClassModal;